import { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
// @mui
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import LoadingButton from '@mui/lab/LoadingButton';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
//
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import { IftaFiling } from 'src/models';
//
import StepTitle from './step-title';
import LinesEditor, { EditsState, LinesEditorHandle } from './lines-editor';
import VehiclesTab from './vehicles-tab';
import FilesTab from './files-tab';
import UploadFileDialog from './upload-file-dialog';
import Iconify from 'src/components/iconify';
import AutomaticReview, { Issue, findIssues } from './automatic-review';
import PinnedIssueCard from './pinned-issue-card';

// ----------------------------------------------------------------------

type TabValue = 'miles' | 'fuel' | 'vehicles' | 'files' | 'review';

type Props = {
  filing: IftaFiling;
  onCalculated: (filing: IftaFiling) => void;
  onBack?: () => void;
  // Where the pinned "Fixing issue" card renders inline on desktop (the wizard
  // header area); on small screens the card floats fixed instead.
  pinnedHost?: RefObject<HTMLDivElement>;
  onPinnedActiveChange?: (active: boolean) => void;
};

export default function StepReview({ filing, onCalculated, onBack, pinnedHost, onPinnedActiveChange }: Props) {
  const [tab, setTab] = useState<TabValue>('review');
  const [payLoading, setPayLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  // Unsaved edits on the line editors: navigating away offers to save them.
  const [edits, setEdits] = useState<EditsState>({ count: 0, invalid: false });
  // Seeded from the filing so acknowledgements survive refreshes and revisits;
  // every toggle is persisted server-side.
  const [ackIssues, setAckIssues] = useState<Record<string, boolean>>(() =>
    Object.fromEntries((filing.acknowledgedIssues || []).map((id) => [id, true]))
  );

  const acknowledgeIssue = (id: string, value: boolean) => {
    setAckIssues((current) => ({ ...current, [id]: value }));
    axios
      .post(formatEndpoint(endpoints.filings.acknowledgeIssue, { id: filing.id }), {
        issueId: id,
        acknowledged: value,
      })
      .then((response) => onCalculated(response.data.filing))
      .catch(() => {
        setAckIssues((current) => ({ ...current, [id]: !value }));
        enqueueSnackbar('Could not save your answer. Please try again.', { variant: 'error' });
      });
  };
  const [pendingNav, setPendingNav] = useState<(() => void) | null>(null);
  const [navSaving, setNavSaving] = useState(false);
  const [editorResetKey, setEditorResetKey] = useState(0);
  // One truck filter for both the Miles and Fuel tabs.
  const [truckFilter, setTruckFilter] = useState<string | null>(null);
  // The issue being fixed right now, pinned over the page while tab-hopping.
  const [pinnedIssue, setPinnedIssue] = useState<Issue | null>(null);
  const editorRef = useRef<LinesEditorHandle>(null);

  const liveIssues = useMemo(() => findIssues(filing), [filing]);
  const pinnedResolved = pinnedIssue !== null && !liveIssues.some((issue) => issue.id === pinnedIssue.id);

  const nextIssue = useMemo(() => {
    if (!pinnedIssue) {
      return null;
    }
    const index = liveIssues.findIndex((issue) => issue.id === pinnedIssue.id);
    const ordered = [...liveIssues.slice(index + 1), ...liveIssues.slice(0, Math.max(index, 0))];
    return ordered.find((issue) => !ackIssues[issue.id]) || null;
  }, [liveIssues, pinnedIssue, ackIssues]);

  useEffect(() => {
    onPinnedActiveChange?.(pinnedIssue !== null);
    return () => onPinnedActiveChange?.(false);
  }, [pinnedIssue, onPinnedActiveChange]);

  // Jumping to an issue = right tab + truck filter focused on its truck, so
  // the table shows exactly the lines the issue is about.
  const focusIssue = (issue: Issue) => {
    setPinnedIssue(issue);
    setTab(issue.fixTab);
    setTruckFilter(issue.truckNumber || null);
  };

  const pinIssue = (issue: Issue) => {
    focusIssue(issue);
  };

  const pinnedNext = () => {
    const next = nextIssue;
    guardedNavigate(() => {
      if (next) {
        focusIssue(next);
      } else {
        setPinnedIssue(null);
        setTruckFilter(null);
        setTab('review');
      }
    });
  };

  const guardedNavigate = (action: () => void) => {
    if (edits.count > 0 && (tab === 'miles' || tab === 'fuel')) {
      setPendingNav(() => action);
      return;
    }
    action();
  };

  const navDiscard = () => {
    setEdits({ count: 0, invalid: false });
    setEditorResetKey((key) => key + 1);
    pendingNav?.();
    setPendingNav(null);
  };

  const navSave = async () => {
    setNavSaving(true);
    try {
      const saved = await editorRef.current?.save();
      if (saved) {
        setEdits({ count: 0, invalid: false });
        pendingNav?.();
        setPendingNav(null);
      }
    } finally {
      setNavSaving(false);
    }
  };

  const guardedBack = onBack ? () => guardedNavigate(onBack) : undefined;

  const pay = async () => {
    setPayLoading(true);
    try {
      const response = await axios.post(formatEndpoint(endpoints.filings.checkout, { id: filing.id }));
      if (response.data.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
        return;
      }
      enqueueSnackbar('This filing is already paid', { variant: 'info' });
    } catch (error) {
      enqueueSnackbar('We could not start the payment. Please try again.', { variant: 'error' });
      setPayLoading(false);
    }
  };

  return (
    <Stack spacing={3}>
      <StepTitle title="Review & unlock your filing" onBack={guardedBack} />

      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Tabs
          value={tab}
          onChange={(_, value) => guardedNavigate(() => setTab(value))}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ flexGrow: 1, minWidth: 0 }}
        >
          <Tab label="Automatic Review" value="review" data-testid="tab-review" />
          <Tab label="Miles" value="miles" data-testid="tab-miles" />
          <Tab label="Fuel" value="fuel" data-testid="tab-fuel" />
          <Tab label="Vehicles" value="vehicles" data-testid="tab-vehicles" />
          <Tab label="Files" value="files" data-testid="tab-files" />
        </Tabs>
        {(tab === 'miles' || tab === 'fuel') && (
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<Iconify icon="solar:cloud-upload-bold" width={16} />}
            onClick={() => setUploadOpen(true)}
            sx={{ flexShrink: 0 }}
            data-testid={`${tab}-upload-more`}
          >
            Upload file
          </Button>
        )}
      </Stack>

      {tab === 'miles' && (
        <LinesEditor
          ref={editorRef}
          key={`miles-${editorResetKey}`}
          filing={filing}
          kind="miles"
          onSaved={onCalculated}
          onEditsChange={setEdits}
          truckFilter={truckFilter}
          onTruckFilterChange={setTruckFilter}
        />
      )}
      {tab === 'fuel' && (
        <LinesEditor
          ref={editorRef}
          key={`fuel-${editorResetKey}`}
          filing={filing}
          kind="fuel"
          onSaved={onCalculated}
          onEditsChange={setEdits}
          truckFilter={truckFilter}
          onTruckFilterChange={setTruckFilter}
        />
      )}
      {tab === 'vehicles' && <VehiclesTab filing={filing} />}
      {tab === 'files' && <FilesTab filing={filing} />}
      {tab === 'review' && (
        <AutomaticReview
          filing={filing}
          onCalculated={onCalculated}
          onFix={pinIssue}
          onPay={pay}
          payLoading={payLoading}
          acknowledged={ackIssues}
          onAcknowledge={acknowledgeIssue}
        />
      )}

      {pinnedIssue && (
        <PinnedIssueCard
          issue={pinnedIssue}
          host={pinnedHost}
          resolved={pinnedResolved}
          acknowledged={Boolean(ackIssues[pinnedIssue.id])}
          onAcknowledge={(value) => acknowledgeIssue(pinnedIssue.id, value)}
          hasNext={nextIssue !== null}
          onNext={pinnedNext}
          onClose={() => setPinnedIssue(null)}
        />
      )}

      {(tab === 'miles' || tab === 'fuel') && (
        <UploadFileDialog
          open={uploadOpen}
          kind={tab}
          filing={filing}
          onClose={() => setUploadOpen(false)}
          onUploaded={(updated) => {
            setEdits({ count: 0, invalid: false });
            setEditorResetKey((key) => key + 1);
            onCalculated(updated);
          }}
        />
      )}

      <Dialog open={pendingNav !== null} onClose={() => setPendingNav(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          You have {edits.count} unsaved edit{edits.count === 1 ? '' : 's'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Would you like to save them before leaving this tab?
            {edits.invalid ? ' Some lines are missing a state, so they can only be discarded.' : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setPendingNav(null)} data-testid="nav-keep-editing">
            Keep editing
          </Button>
          <Button color="inherit" onClick={navDiscard} data-testid="nav-discard-edits">
            Discard
          </Button>
          <LoadingButton
            variant="contained"
            color="info"
            loading={navSaving}
            disabled={edits.invalid}
            onClick={navSave}
            sx={{ color: '#FFFFFF' }}
            data-testid="nav-save-edits"
          >
            Save changes
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
